import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import { ANALYTICS_ATTRIBUTE_NAME, ANALYTICS_KEY } from '../definitions/Analytics';
import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import MessagePropType from '../definitions/MessagePropType';
import { ProductCardLayoutContext } from '../hooks/useProductCardLayout';
import ProductCardName from './ProductCardName';
import ProductCardDetails from './ProductCardDetails';


function ProductCardLayout({
    image,
    name,
    topRightElement,

    details,
    rightOfDetailsElement,

    children,
    footer,

    className,
    designationMessage,
    messageBanner,
    analyticsObject,
    borderColour,
    hasBorderRadius = true,
    isRightColumnCondensed = false,
    hasUniformPadding = true,

    ...otherProps
}) {
    // define shared context measurements for ProductCardContext
    const [ nameHeight, setNameHeight ] = useState(45);
    const [ topRightHeight, setTopRightHeight ] = useState(80);

    const measurements = {
        nameHeight, setNameHeight,
        topRightHeight, setTopRightHeight,
    };

    const doesDetailsNeedSeparator = footer || React.Children.toArray(children).length;

    return (
        <ProductCardLayoutContext.Provider value={measurements}>
            <Container
                ref={self => self && (self[ANALYTICS_KEY] = analyticsObject)}
                imageWidth={image?.width}
                imageHeight={image?.height || image?.width}
                isRightColumnCondensed={isRightColumnCondensed}
                borderColour={borderColour}
                hasBorderRadius={hasBorderRadius}
                className={classNames(
                    'rh-position-relative', 'rh-display-flex', 'rh-flex-direction-column', 'rh-bg-coconut', {
                        [ANALYTICS_ATTRIBUTE_NAME]: !!analyticsObject,
                        'details-bottom-separator': doesDetailsNeedSeparator,
                        'rh-border-radius-8px': hasBorderRadius,
                    },
                    className,
                )}
                data-test-name="product-card-layout"
                {...otherProps}
            >
                <If condition={messageBanner}>
                    <p className={classNames('message-banner', 'rh-text-xs', 'rh-m-0', 'rh-py-0_25', 'rh-px-1',
                        'rh-text-align-center', 'rh-bg-grape-lightest', {
                            'has-border-radius': hasBorderRadius,
                        })}
                    >
                        {messageBanner}
                    </p>
                </If>

                <div
                    className={classNames('inner-container', {
                        'has-uniform-padding': hasUniformPadding,
                    })}
                >
                    <ProductCardName
                        designationMessage={designationMessage}
                        image={image}
                        name={name}
                    >
                        {topRightElement}
                    </ProductCardName>

                    <ProductCardDetails details={details}>
                        {rightOfDetailsElement}
                    </ProductCardDetails>

                    <If condition={children}>
                        {children}
                    </If>

                </div>
                <If condition={footer}>
                    <div className="footer rh-bg-stone-lightest">
                        {footer}
                    </div>
                </If>
            </Container>
        </ProductCardLayoutContext.Provider>
    );
}

ProductCardLayout.propTypes = {
    image: PropTypes.shape({
        url: PropTypes.string.isRequired,
        alt: MessagePropType.isRequired,
        width: PropTypes.string.isRequired,
        height: PropTypes.string,
    }),
    name: PropTypes.shape({
        message: MessagePropType,
        Component: PropTypes.node,
    }).isRequired,
    topRightElement: PropTypes.node,

    details: PropTypes.node.isRequired,

    rightOfDetailsElement: PropTypes.node,

    children: PropTypes.node,
    footer: PropTypes.node,

    className: PropTypes.string,
    designationMessage: PropTypes.node,
    messageBanner: PropTypes.node,
    analyticsObject: PropTypes.object.isRequired,
    borderColour: PropTypes.oneOf(Object.values(Colours)),
    hasBorderRadius: PropTypes.bool,
    isRightColumnCondensed: PropTypes.bool,
    hasUniformPadding: PropTypes.bool,
};


const PRODUCT_CARD_HORIZONTAL_PADDING_PERCENTAGE = '2.3%';
const PRODUCT_CARD_HORIZONTAL_PADDING_FIXED = Sizes.SPACING.ONE_AND_THREE_QUARTERS;


function getFlexLeft(isMoreNarrow) {
    return `flex: 11 1 ${isMoreNarrow ? '25.5em ' : '20em '};`;   // math be damned, 25.5 works better than 25
}

function getFlexRight(isMoreNarrow) {
    return `flex: 2 1 ${isMoreNarrow ? '7em ' : '12em '};`;
}

// some CSS styles also provided by ProductCardDetails in order to provide the details offset amount
const Container = styled.div`
    box-shadow: 1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 1px 2px 1px ${Colours.STONE_LIGHT},
        -1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 -1px 2px 1px ${Colours.STONE_LIGHT};

    border: 1px solid ${props => props.borderColour
        ? props.borderColour
        : Colours.STONE_DARK};

    .message-banner.has-border-radius {
        border-top-left-radius: 7px;    // assumption: when present, the border radius is 8px
        border-top-right-radius: 7px;
    }

    > .inner-container {
        padding: ${Sizes.SPACING.ONE_AND_THREE_QUARTERS} ${PRODUCT_CARD_HORIZONTAL_PADDING_PERCENTAGE} 0 ${PRODUCT_CARD_HORIZONTAL_PADDING_PERCENTAGE};

        &.has-uniform-padding {
            padding: ${Sizes.SPACING.ONE_AND_THREE_QUARTERS};
        }

        > .row {
            column-gap: ${Sizes.SPACING.ONE_AND_A_HALF};

            > .name-wrapper {
                ${({ isRightColumnCondensed }) => `${getFlexLeft(isRightColumnCondensed)};`}

                > .product-card-name {
                    > .product-card-name-logo {
                        ${({ imageWidth, imageHeight }) => `
                            width: ${imageWidth};
                            height: ${imageHeight};
                        `}
                    }
                }
            }

            > .top-right-wrapper {
                ${({ isRightColumnCondensed }) => `${getFlexRight(isRightColumnCondensed)};`}
            }
        }

        > .row-reverse {
            column-gap: ${Sizes.SPACING.ONE_AND_A_HALF};

            > .details-wrapper {
                ${({ isRightColumnCondensed }) => `${getFlexLeft(isRightColumnCondensed)};`}
            }

            > .right-hand-side-wrapper {
                ${({ isRightColumnCondensed }) => `${getFlexRight(isRightColumnCondensed)};`}
            }
        }

    }

    > .footer {
        // hasBorderRadius is a true/false value; no need to parameterize this atm
        ${({ hasBorderRadius }) => hasBorderRadius && 'border-radius: 0 0 7px 7px;'}
    }

    &.details-bottom-separator {
        > .inner-container {
            > .row-reverse {
                > .details-wrapper {
                    // vertical spacing not required if no children or footer
                    margin-bottom: ${Sizes.SPACING.TWO};
                }
            }
        }
    }
`;

export { PRODUCT_CARD_HORIZONTAL_PADDING_FIXED, PRODUCT_CARD_HORIZONTAL_PADDING_PERCENTAGE };
export default ProductCardLayout;
